<script lang="ts">
  import { goto, replaceState } from '$app/navigation';
  import Crown from '$icons/CrownIcon.svelte';
  import SeedLogo from '$icons/SeedLogo.svelte';
  import LoaderRows from '$lib/components/common/loaders/LoaderRows.svelte';
  import CollapseSection from '$lib/components/common/CollapseSection.svelte';
  import UserAvatarWrapper from '$lib/components/common/user/userAvatarWrapper.svelte';
  import relativeTime from 'dayjs/plugin/relativeTime';

  import { getPostDonations } from '$src/utils/api/donation.util';
  import dayjs from 'dayjs';
  import type { WithCreatorData } from 'garden-common';
  import type { FPost } from 'garden-common';
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  export let post: Partial<FPost<string>> & Partial<WithCreatorData>;
  dayjs.extend(relativeTime);

  let loading: boolean = true;
  let donations: any[] = [];
  let firstPlace: any = null;
  onMount(async () => {
    donations = await getPostDonations({ createdAt: -1 }, post._id);
    donations.sort((a, b) => b.donationValue - a.donationValue);
    firstPlace = donations[0];
    loading = false;
  });
</script>

<CollapseSection contentOpen>
  <div slot="title" class="font-medium text-blueish-grey-900">
    <div class="flex gap-2 items-center">
      <SeedLogo size="24" />
      <span class="text-blueish-grey-900 font-medium">{post?.totalDonationsSeed} seeds donated</span
      >
    </div>
  </div>
  <div
    class="border-t border-grey-transparency-dark border-opacity-5 pt-4 mt-4 flex flex-col gap-4"
  >
    <div class="flex flex-col gap-10 pl-4">
      {#if loading}
        <LoaderRows rows={1} />
      {:else if firstPlace}
        <div in:fade|global class="flex flex-col gap-2 max-w-lg">
          <span class="text-accent-blue-70 text-xs font-bold">Highest donation</span>

          <div
            class="flex justify-between hover:bg-blueish-grey-200 cursor-pointer p-2 px-4 rounded-14 transition-all"
            role="button"
            tabindex="0"
            on:click={() => {
              window.location.href = '/profile/' + firstPlace?.donator?.userName;
            }}
            on:keydown={e => {
              if (e.key === 'Enter') {
                window.location.href = '/profile/' + firstPlace?.donator?.userName;
              }
            }}
          >
            <div class="flex gap-5 items-center">
              <button class="relative grid place-items-center">
                <UserAvatarWrapper
                  profileImage={firstPlace?.donator?.profileImage}
                  userName={firstPlace?.donator?.userName}
                />

                <div class="absolute">
                  <Crown size="65" />
                </div>
              </button>

              <button
                class="flex flex-col gap-1"
                on:click={() => {
                  if (!firstPlace?.donator?.userName)
                    goto('/profile/' + firstPlace?.donator?.userName);
                }}
              >
                <span class="text-accent-blue-80 text-sm text-left font-medium"
                  >{firstPlace?.donator?.name || 'Deleted'}</span
                >

                <div class="flex gap-1 items-center text-blueish-grey-600 text-xs">
                  <span class="">@{firstPlace?.donator?.userName}</span>
                  <span>•</span>
                  {#if firstPlace?.createdAt}
                    <span>{dayjs(firstPlace?.createdAt).fromNow()}</span>
                  {/if}
                </div>
              </button>
            </div>

            <div class="flex items-center">
              {#if firstPlace.donationValue}
                <div class="flex items-center gap-1">
                  <SeedLogo size="24" />
                  <span class="text-blueish-grey-800 font-medium">
                    {firstPlace.donationValue} seeds
                  </span>
                </div>
              {/if}
              {#if firstPlace.itemDonatedToDetails?.compressedThumbnailURLS[0]}
                <div>
                  <img
                    class="object-cover w-14 h-12 rounded-lg"
                    alt="Post preview."
                    src={firstPlace.itemDonatedToDetails.compressedThumbnailURLS[0]}
                  />
                </div>
              {/if}
            </div>
          </div>

          {#each donations as donation, i}
            {#if i !== 0}
              <div
                class="flex justify-between hover:bg-blueish-grey-200 cursor-pointer rounded-14 p-2 px-4 transition-all"
                role="button"
                tabindex="0"
                on:click={() => {
                  window.location.href = '/profile/' + donation?.donator?.userName;
                }}
                on:keydown={e => {
                  if (e.key === 'Enter') {
                    window.location.href = '/profile/' + donation?.donator?.userName;
                  }
                }}
              >
                <div class="flex gap-5 items-center">
                  <button class="relative grid place-items-center">
                    <UserAvatarWrapper
                      profileImage={donation?.donator?.profileImage}
                      userName={donation?.donator?.userName}
                    />
                  </button>

                  <button
                    class="flex flex-col gap-1"
                    on:click={() => {
                      if (!donation?.donator?.userName)
                        goto('/profile/' + donation?.donator?.userName);
                    }}
                  >
                    <span class="text-accent-blue-80 text-sm text-left font-medium"
                      >{donation?.donator?.name || 'Deleted'}</span
                    >

                    <div class="flex gap-1 items-center text-blueish-grey-600 text-xs">
                      <span class="">@{donation?.donator?.userName}</span>
                      <span>•</span>
                      {#if donation?.createdAt}
                        <span>{dayjs(donation?.createdAt).fromNow()}</span>
                      {/if}
                    </div>
                  </button>
                </div>

                <div class="flex items-center">
                  {#if donation.donationValue}
                    <div class="flex items-center gap-1">
                      <SeedLogo size="24" />
                      <span class="text-blueish-grey-800 font-medium">
                        {donation.donationValue} seeds
                      </span>
                    </div>
                  {/if}

                  <div>
                    {#if donation.itemDonatedToDetails?.compressedThumbnailURLS[0]}
                      <img
                        class="object-cover w-14 h-12 rounded-lg"
                        alt="Post preview."
                        src={donation.itemDonatedToDetails.compressedThumbnailURLS[0]}
                      />
                    {/if}
                  </div>
                </div>
              </div>
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  </div>
</CollapseSection>
